/**
 * Parsing for the placeholder element content editors place in Index (WordPress) post bodies:
 *
 *   <div data-component="product-carousel" data-category-id="4761" data-title="Your Title"></div>
 *
 * It is a plain `div` carrying `data-` attributes because WordPress strips tags and attributes it
 * does not recognise on save — a `<product-carousel>` element never survived the editor.
 *
 * The body reaches us as an HTML string rendered through v-html, where the placeholder could not
 * resolve as a Vue component on its own. We split the string on each placeholder instead and let
 * the caller render the HTML chunks and the carousels in order, which keeps each carousel in
 * place. Because the placeholder is a complete element, removing it leaves balanced markup either
 * side.
 */

const COMPONENT_NAME = 'product-carousel'

/**
 * An empty div whose opening tag carries `data-component`. The lookahead keeps the scan off every
 * other div in the post, and `[^<]*` tolerates stray whitespace inside the placeholder without
 * swallowing a nested element.
 */
const PLACEHOLDER_PATTERN = /<div\b(?=[^>]*\bdata-component\s*=)([^>]*)>[^<]*<\/div\s*>/gi

// Cheap test used to keep placeholder-free posts on the original single-chunk path.
const ANY_PLACEHOLDER_PATTERN = /<div\b[^>]*\bdata-component\s*=\s*["']?product-carousel\b/i

// Standard HTML attribute quoting. A bare value runs to whitespace or the end of the tag.
const attributeValue = (name: string) => new RegExp(
  `\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`,
  'i'
)

const COMPONENT_PATTERN = attributeValue('data-component')
const CATEGORY_ID_PATTERN = attributeValue('data-category-id')
const TITLE_PATTERN = attributeValue('data-title')

// Attribute values arrive HTML-encoded: `&amp;` for an ampersand, `&quot;` for a quote, and the
// numeric entities WordPress substitutes for smart punctuation.
const ENTITY_PATTERN = /&(#x?[0-9a-f]+|[a-z]+);/gi
const NAMED_ENTITIES: Record<string, string> = {
  amp  : '&',
  lt   : '<',
  gt   : '>',
  quot : '"',
  apos : "'",
  nbsp : ' ',
}

const MAX_CODE_POINT = 0x10ffff

/**
 * Safety net for a placeholder that some block has wrapped in a container of its own. Splitting
 * inside that container would strand its opening and closing tags in the chunks either side. It
 * only fires when the container holds nothing but the placeholder, so a wrapper an editor added
 * deliberately around other content is left alone.
 */
const BLOCK_PADDING = '(?:\\s|&nbsp;|<br\\s*/?>)*'
const WRAPPED_PLACEHOLDER_PATTERN = new RegExp(
  `<(p|div)[^>]*>${BLOCK_PADDING}(<div\\b[^>]*\\bdata-component\\s*=[^>]*>[^<]*</div\\s*>)${BLOCK_PADDING}</\\1\\s*>`,
  'gi'
)

// A block can nest the placeholder a wrapper or two deep; each pass peels off one layer.
const MAX_WRAPPER_DEPTH = 3

export type PostContentSegment =
  | { type: 'html', html: string }
  | { type: 'carousel', categoryId: number, title?: string }

export function hasProductCarouselTag(content: string | undefined | null): boolean {
  return ANY_PLACEHOLDER_PATTERN.test(content || '')
}

function readAttribute(attributes: string, pattern: RegExp): string | undefined {
  // One capture group per accepted quoting style; only the matching one is defined.
  return pattern.exec(attributes)?.slice(1).find(group => group !== undefined)
}

function unwrapBlocks(content: string): string {
  let unwrapped = content

  for (let depth = 0; depth < MAX_WRAPPER_DEPTH; depth++) {
    const next = unwrapped.replace(WRAPPED_PLACEHOLDER_PATTERN, '$2')
    if (next === unwrapped) {
      break
    }
    unwrapped = next
  }

  return unwrapped
}

function decodeEntities(value: string): string {
  return value.replace(ENTITY_PATTERN, (entity, body: string) => {
    if (body[0] !== '#') {
      return NAMED_ENTITIES[body.toLowerCase()] ?? entity
    }

    const hex = body[1] === 'x' || body[1] === 'X'
    const codePoint = parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10)

    return Number.isFinite(codePoint) && codePoint > 0 && codePoint <= MAX_CODE_POINT
      ? String.fromCodePoint(codePoint)
      : entity
  })
}

function readCategoryId(attributes: string): number | null {
  const raw = readAttribute(attributes, CATEGORY_ID_PATTERN)

  if (!raw) {
    return null
  }

  const categoryId = Number(raw.trim())

  return Number.isInteger(categoryId) && categoryId > 0 ? categoryId : null
}

function readTitle(attributes: string): string | undefined {
  const raw = readAttribute(attributes, TITLE_PATTERN)

  if (!raw) {
    return undefined
  }

  return decodeEntities(raw).trim() || undefined
}

/**
 * Split post content into the HTML either side of each carousel placeholder. A placeholder whose
 * category id is missing or unusable is dropped rather than rendered, so the post reads as though
 * it were never there; `onInvalidTag` is called with the raw element so the caller can log it.
 */
export function parsePostContent(
  content: string | undefined | null,
  onInvalidTag?: (tag: string) => void
): PostContentSegment[] {
  if (!content) {
    return []
  }

  if (!hasProductCarouselTag(content)) {
    return [{ type: 'html', html: content }]
  }

  const unwrapped = unwrapBlocks(content)
  const segments: PostContentSegment[] = []
  let cursor = 0
  let match: RegExpExecArray | null

  PLACEHOLDER_PATTERN.lastIndex = 0

  while ((match = PLACEHOLDER_PATTERN.exec(unwrapped)) !== null) {
    const attributes = match[1]

    // A placeholder for some other component is left in the HTML untouched, so the cursor does not
    // move and the element stays part of the surrounding chunk.
    if (readAttribute(attributes, COMPONENT_PATTERN)?.trim().toLowerCase() !== COMPONENT_NAME) {
      continue
    }

    const html = unwrapped.slice(cursor, match.index)
    cursor = match.index + match[0].length

    if (html) {
      segments.push({ type: 'html', html })
    }

    const categoryId = readCategoryId(attributes)

    if (categoryId === null) {
      onInvalidTag?.(match[0])
      continue
    }

    const title = readTitle(attributes)

    segments.push(title ? { type: 'carousel', categoryId, title } : { type: 'carousel', categoryId })
  }

  const tail = unwrapped.slice(cursor)

  if (tail) {
    segments.push({ type: 'html', html: tail })
  }

  return segments
}
